/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { useWindowSize } from '../../../helpers';
import { H2, H3 } from '../../Typography';
import { Asterisk, Button, Slidernation } from '../../UI';
import SlideShow from '../../UI/Slideshow';
import useStyle from './style';

const numberWithCommas = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export type RatingsType = {
  bestCompany: number;
  google: number;
  trustpilot: number;
  numberOfReviewsGoogle: number;
  numberOfReviewsTrustpilot: number;
  numberOfReviewsBestCompany: number;
};

interface CustomerRatingsProps {
  /**
   * The ratings object containing average rating and total reviews.
   */
  ratings: RatingsType;

  /**
   * The title to display (optional).
   */
  title?: string;

  /**
   * Whether to show a link to the reviews page (optional).
   */
  reviewsLink?: boolean;

  /**
   * Whether to hide the component header (optional).
   */
  noHeader?: boolean;

  /**
   * Is the parent a package page (optional)
   */
  packagePage?: boolean;

  /**
   * Whether to use a slideshow for ratings on mobile (optional).
   */
  mobileSlideshow?: boolean;

  /**
   * Variant of the component.
   */
  variant?: 'base' | 'quincy' | 'mobile-border';

  /**
   * Size of the header.
   */
  headerSize?: 'h2' | 'h3';
}

const CustomerRatings: React.FC<CustomerRatingsProps> = ({
  ratings,
  title = 'The #1 customer-rated home security system.',
  reviewsLink = false,
  noHeader = false,
  packagePage = false,
  mobileSlideshow = false,
  variant = 'base',
  headerSize = 'h2',
}) => {
  const {
    componentWrapper,
    reviewsWrapper,
    reviewStyle,
    reviewLogo,
    reviewsStars,
    reviewNumberStyle,
    slideshowWrapper,
    quincyStyles,
    quincyReviewsWrapper,
    quincyStars,
    quincyLogo,
    mobileBorderStyles,
    mobileBorderWrapper,
  } = useStyle();
  const {
    bestCompany,
    google,
    trustpilot,
    numberOfReviewsGoogle,
    numberOfReviewsTrustpilot,
    numberOfReviewsBestCompany,
  } = ratings || {
    bestCompany: 0,
    google: 0,
    trustpilot: 0,
    numberOfReviewsGoogle: 0,
    numberOfReviewsTrustpilot: 0,
    numberOfReviewsBestCompany: 0,
  };

  const [currSlide, setCurrSlide] = useState(0);
  const { width = 0 } = useWindowSize();
  const isQuincy = variant === 'quincy';
  const isMobileBorder = variant === 'mobile-border';
  const isH3 = headerSize === 'h3';

  const ratingCards = [
    {
      company: 'google',
      logo: {
        url: 'https://www.datocms-assets.com/10154/1685475459-google-logo.png',
        alt: 'google-svg-icon',
        width: 72,
        height: 24,
      },
      stars: google,
      reviews: numberWithCommas(numberOfReviewsGoogle),
    },
    {
      company: 'trustpilot',
      logo: {
        url: 'https://www.datocms-assets.com/10154/1685475483-trustpilot-logo.png',
        alt: 'trustpilot-svg-icon',
        width: 95,
        height: 21,
      },
      stars: trustpilot,
      reviews: numberWithCommas(numberOfReviewsTrustpilot),
    },
    {
      company: 'bestcompany',
      logo: {
        url: 'https://www.datocms-assets.com/10154/1685475451-best-company-logo.png',
        alt: 'bestcompany-icon',
        width: 120,
        height: 24,
      },
      stars: bestCompany,
      reviews: numberWithCommas(numberOfReviewsBestCompany),
    },
  ];

  return (
    <div
      css={[
        componentWrapper,
        isQuincy && quincyStyles,
        isMobileBorder && mobileBorderStyles,
      ]}
    >
      {noHeader ? (
        <></>
      ) : isH3 ? (
        <H3>
          {title}
          <Asterisk>
            *When compared to other national, leading DIY home security brands
            as of Aug 2024 on TrustPilot and Google Reviews
          </Asterisk>
        </H3>
      ) : packagePage ? (
        <H2 style={{ textAlign: 'center' }}>{title}</H2>
      ) : (
        <H2>
          {title}
          <Asterisk>
            *When compared to other national, leading DIY home security brands
            as of Aug 2024 on TrustPilot and Google Reviews
          </Asterisk>
        </H2>
      )}
      <div
        css={[
          reviewsWrapper,
          isQuincy && quincyReviewsWrapper,
          isMobileBorder && mobileBorderWrapper,
        ]}
        style={{ marginTop: noHeader ? 0 : 35 }}
      >
        {mobileSlideshow && width <= 900 ? (
          <div css={slideshowWrapper}>
            <SlideShow
              autoPlay
              currSlide={currSlide}
              runOnChange={slide => setCurrSlide(slide)}
              slides={ratingCards.map((ratings, idx) => {
                const { logo, stars, reviews } = ratings;

                return (
                  <div css={reviewStyle} key={`customer-rating-mobile-${idx}`}>
                    <div css={[reviewsStars, isQuincy && quincyStars]}>
                      <img
                        src="https://www.datocms-assets.com/10154/1657734954-green-stars.svg"
                        alt="stars-icon"
                        width={120}
                        height={24}
                      />
                      <p>{stars}/5</p>
                    </div>
                    <div css={[reviewLogo, isQuincy && quincyLogo]}>
                      <img
                        src={logo.url}
                        alt={logo.alt}
                        width={logo.width}
                        height={logo.height}
                        loading="lazy"
                      />
                    </div>
                    <div css={reviewNumberStyle}>
                      <i>{reviews}+ reviews</i>
                    </div>
                  </div>
                );
              })}
            />
            <Slidernation
              rounded
              darkTabs
              bordered
              slideCount={ratingCards.length}
              activeSlide={currSlide}
              setSlide={(slide: number) => setCurrSlide(slide)}
            />
          </div>
        ) : (
          <>
            {ratingCards.map((ratings, idx) => {
              const { logo, stars, reviews } = ratings;

              return (
                <div css={reviewStyle} key={`review-ratings-${idx}`}>
                  <div css={[reviewsStars, isQuincy && quincyStars]}>
                    <img
                      src="https://www.datocms-assets.com/10154/1657734954-green-stars.svg"
                      alt="stars-icon"
                      width={120}
                      height={24}
                    />
                    <p>{stars}/5</p>
                  </div>
                  <div css={[reviewLogo, isQuincy && quincyLogo]}>
                    <img
                      src={logo.url}
                      alt={logo.alt}
                      width={logo.width}
                      height={logo.height}
                    />
                  </div>
                  <div css={reviewNumberStyle}>
                    <i>{reviews}+ reviews</i>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      {reviewsLink && (
        <Button
          color="purple"
          cypressId="read-reviews"
          style="text"
          href="/reviews/"
          isUnderlined
          css={css`
            margin-top: 50px;
          `}
        >
          Read the Reviews
        </Button>
      )}
    </div>
  );
};

export default CustomerRatings;
